import React, { useEffect, useState } from 'react';
import useApi from '../../hooks/useApi';
import { ManagedUser, Role } from '../../types';
import styles from './EditRolesModal.module.css';

interface EditRolesModalProps {
  user: ManagedUser;
  roles: Role[];
  onClose: () => void;
  onRolesUpdated: (user: ManagedUser) => void;
}

const EditRolesModal: React.FC<EditRolesModalProps> = ({
  user,
  roles,
  onClose,
  onRolesUpdated,
}) => {
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [currentUserRoleName, setCurrentUserRoleName] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const { securedRequest, loading } = useApi();

  const filteredRoles = roles.filter(role => {
    if (role.name === 'LocaliQ-Partner-Admin') {
      return currentUserRoleName === 'LocaliQ-Partner-Admin';
    }
    return true;
  });

  useEffect(() => {
    const controller = new AbortController();
    const timer = new Promise(resolve => setTimeout(resolve, 600));
    const fetchData = securedRequest('get', `/api/admin/users/${encodeURIComponent(user.userId)}/roles`, null, controller.signal)
      .then(({ data }) => {
        if (data && Array.isArray(data)) {
          const userRoles = data as Role[];
          // The `roles` prop contains the list of all valid partner roles.
          // We find the role assigned to the user that is also in our list of partner roles.
          const partnerRoleIds = roles.map(r => r.id);
          const currentPartnerRole = userRoles.find(ur => partnerRoleIds.includes(ur.id));

          if (currentPartnerRole) {
            setSelectedRole(currentPartnerRole.id);
            setCurrentUserRoleName(currentPartnerRole.name);
          }
        }
      });

    Promise.all([fetchData, timer]).finally(() => setInitialLoading(false));

    return () => controller.abort();
  }, [user.userId, securedRequest, roles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const { data, error } = await securedRequest(
      'put',
      `/api/admin/users/${encodeURIComponent(user.userId)}/roles`,
      { roles: selectedRole ? [selectedRole] : [] }
    );

    if (error) {
      const message = typeof error === 'object' && error.error ? error.error : 'Failed to update roles.';
      setFormError(message);
      return;
    }

    onRolesUpdated(data);
    onClose();
  };

  return (
    <div className={styles.modalBackdrop}>
      <div className={`${styles.modalContent} card`}>
        <div className="card-body">
          <h5 className="card-title">Edit Roles</h5>
          <p className="text-muted mb-3">{user.name} &mdash; {user.email}</p>
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Roles</label>
              <div className={`${styles.roleList} d-flex ${initialLoading ? 'justify-content-center align-items-center' : 'flex-column'}`}>
                {initialLoading ? (
                  <div className="text-center">
                    <div className="spinner-border spinner-border-sm" role="status" />
                    <span className="ms-2">Loading...</span>
                  </div>
                ) : (
                  <>
                    {filteredRoles.length === 0 && (
                      <small className="text-muted">No roles configured.</small>
                    )}
                    {filteredRoles.map(role => (
                      <div key={role.id} className="form-check w-100">
                        <input
                          id={`edit-role-${role.id}`}
                          type="radio"
                          name="userRole"
                          className="form-check-input"
                          checked={selectedRole === role.id}
                          onChange={() => setSelectedRole(role.id)}
                        />
                        <label htmlFor={`edit-role-${role.id}`} className="form-check-label">
                          {role.name}
                          {role.description && (
                            <small className={`text-muted ms-1 ${styles.roleDescription}`}>{role.description}</small>
                          )}
                        </label>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
            {formError && <div className="alert alert-danger py-2">{formError}</div>}
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading || initialLoading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditRolesModal;
